import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
// import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { createAsteroidGeometry } from './AsteroidGeometry';

// Init renderer (must be created before detectSupport)
// const renderer = new THREE.WebGLRenderer();

// Init KTX2Loader
// const ktx2Loader = new KTX2Loader();
// ktx2Loader.setTranscoderPath('/basis/');
// ktx2Loader.detectSupport(renderer);

// Texture loaders
const textureLoader = new THREE.TextureLoader();
const imageLoader = new THREE.ImageLoader();

// Setup DRACO loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('/draco/'); // Ensure this path is correct

// Setup GLTF loader with DRACO + KTX2
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);
// gltfLoader.setKTX2Loader(ktx2Loader);

// Cache and dedupe maps
const cache = new Map();
const pendingLoads = new Map();

const loaders = {
  image: imageLoader,
  texture: async (url) => {
    // console.log('Starting texture load for', url);
    // if (url.endsWith('.ktx2')) {
    //   await ktx2Loader.transcoderPending; // Ensure transcoder is ready
    //   return new Promise((resolve, reject) => {
    //     ktx2Loader.load(url, (texture) => {
    //       console.log('KTX2 texture loaded:', texture);
    //       resolve(texture);
    //     }, undefined, (error) => {
    //       console.error('KTX2 load error:', error);
    //       reject(error);
    //     });
    //   });
    // } else {
      return new Promise((resolve, reject) => {
        textureLoader.load(url, (texture) => {
          // console.log('Standard texture loaded:', texture);
          resolve(texture);
        }, undefined, (error) => {
          console.error('Standard texture load error:', error);
          reject(error);
        });
      });
    },
  // },
  gltf: gltfLoader,
  geometry: async (url) => {
    if (url.startsWith('sphere:')) {
      const params = url.slice('sphere:'.length).split(',').map(Number);
      return new THREE.SphereGeometry(...params);
    }
    if (url.startsWith('asteroid:')) {
      const params = url
        .slice('asteroid:'.length)
        .split(',')
        .map(Number);

      const [
        radius = 0.05,
        detail = 0,
        deformation = 0.4,
        seed = Math.random(),
      ] = params;

      return createAsteroidGeometry({ radius, detail, deformation, seed });
    }

    throw new Error(`Unknown procedural geometry type: ${url}`);
  },
};

/**
 * Generic load function with caching and deduplication.
 * @param {'image' | 'texture' | 'gltf' | 'geometry'} type
 * @param {string} key
 * @param {string} url
 * @returns {Promise<any>}
 */

export async function loadAsset(type, key, url) {
  // if (cache.has(key)) return cache.get(key);
  // console.log(`[loadAsset] type: ${type}, key: ${key}, url: ${url}`);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (pendingLoads.has(key)) return pendingLoads.get(key);

  const loader = loaders[type];
  if (!loader) throw new Error(`Unsupported loader type: ${type}`);

  let promise;

  if (typeof loader.load === 'function') {
    // Special case for GLTF path handling
    if (type === 'gltf') {
      const basePath = url.substring(0, url.lastIndexOf('/') + 1);
      loader.setPath(basePath);
      url = url.substring(url.lastIndexOf('/') + 1);
    }

    promise = new Promise((resolve, reject) => {
      // console.log(`[LOAD] ${type} -> ${url}`);
      loader.load(
        url,
        (asset) => {
          // console.log(`[SUCCESS] Loaded ${type}: ${key}`);
          cache.set(key, asset);
          pendingLoads.delete(key);
          resolve(asset);
        },
        undefined,
        (error) => {
          console.error(`[ERROR] Failed to load ${type}: ${url}`, error);
          pendingLoads.delete(key);
          reject(new Error(`Failed to load ${type} from ${url}: ${error.message}`));
        }
      );
    });

  } else if (typeof loader === 'function') {
    // Procedural or promise-based loaders like geometry or our texture wrapper
    promise = Promise.resolve(loader(url))
      .then((asset) => {
        cache.set(key, asset);
        pendingLoads.delete(key);
        return asset;
      })
      .catch((error) => {
        pendingLoads.delete(key);
        throw new Error(`Failed to load ${type} from ${url}: ${error.message}`);
      });
  } else {
    throw new Error(`Invalid loader for type "${type}"`);
  }

  pendingLoads.set(key, promise);
  return promise;
}

/**
 * Preload multiple assets in parallel and return as key -> asset map.
 * @param {Array<{ type: 'image' | 'texture' | 'gltf', key: string, url: string }>} assetList
 * @param {(loadedCount: number, total: number) => void} [onProgress]
 * @returns {Promise<Object>} Map of key -> asset
 */
export async function preloadAssets(assetList, onProgress) {
  let loaded = 0;
  const total = assetList.length;

  const validAssets = assetList.filter(asset => {
    const isValid =
      typeof asset === 'object' &&
      !Array.isArray(asset) &&
      asset?.type &&
      asset?.key &&
      asset?.url;

    if (!isValid) {
      console.warn('[preloadAssets] Skipping invalid asset:', asset);
    }

    return isValid;
  });

  const results = await Promise.all(
    validAssets.map(({ type, key, url }) =>
      loadAsset(type, key, url).then(asset => {
        loaded++;
        if (typeof onProgress === 'function') {
          onProgress(loaded, total);
        }
        return { key, asset };
      })
    )
  );

  return results.reduce((acc, { key, asset }) => {
    acc[key] = asset;
    return acc;
  }, {});
}

/**
 * Clear a single cached asset.
 * @param {string} key
 */
// export function clearAsset(key) {
//   const asset = cache.get(key);
//   if (asset?.dispose) asset.dispose();
//   cache.delete(key);
// }
function disposeObject(obj) {
  obj.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
    }
    if (child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach(disposeMaterial);
      } else {
        disposeMaterial(child.material);
      }
    }
  });

  if (obj.parent) {
    obj.parent.remove(obj);
  }
}

function disposeMaterial(material) {
  for (const key in material) {
    const value = material[key];
    if (value && typeof value.dispose === 'function') {
      value.dispose(); // Dispose textures and maps
    }
  }
  material.dispose();
}

export function clearAsset(key) {
  const asset = cache.get(key);
  if (!asset) return;

  if (asset.scene) {
    // Likely a GLTF asset
    disposeObject(asset.scene);
  } else if (asset.isObject3D || asset.isMesh || asset.isGroup) {
    disposeObject(asset);
  } else if (asset.isBufferGeometry || asset.isMaterial) {
    if (asset.dispose) asset.dispose();
  }
  console.log("disposed: ", asset);
  cache.delete(key);
}


/**
 * Clear all cached assets.
 */
export function clearAllAssets() {
  cache.forEach((asset) => {
    if (asset?.dispose) asset.dispose();
  });
  cache.clear();
}



